import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold01,
  Italic01,
  Strikethrough01,
  Underline01,
  FlipBackward,
  FlipForward,
} from "@untitled-ui/icons-react";

import { useCertificateStore } from "@stores";
import { styles } from "./CertificateEditor.styles";
import { CertificateEditorContent } from "./CertificateEditorContent";
import { CertificateEditorHeader } from "./CertificateEditorHeader";
import { useCertificateEditor } from "./hooks/useCertificateEditor";

export const CertificateEditor = () => {
  const { editor, handleChangeOrientation } = useCertificateEditor();
  const { isDownload } = useCertificateStore();

  const otherOptions = [
    {
      id: "Undo",
      icon: <FlipBackward className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().undo().run(),
    },
    {
      id: "Redo",
      icon: <FlipForward className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().redo().run(),
    },
  ];

  const headingsOptions = [
    {
      id: "NormalText",
      name: `Normal Text`,
      onClick: () => editor?.chain().focus().setParagraph().run(),
      active: editor?.isActive("paragraph"),
    },
    {
      id: "Heading1",
      name: `Heading 1`,
      onClick: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
      active: editor?.isActive("heading", { level: 1 }),
    },
    {
      id: "Heading2",
      name: `Heading 2`,
      onClick: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
      active: editor?.isActive("heading", { level: 2 }),
    },
    {
      id: "Heading3",
      name: `Heading 3`,
      onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
      active: editor?.isActive("heading", { level: 3 }),
    },
  ];

  const fontFamilyOptions = [
    {
      id: "Arial",
      name: `Arial`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Arial").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Arial" }),
    },
    {
      id: "TimesNewRoman",
      name: `Times New Roman`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Times New Roman").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Times New Roman" }),
    },
    {
      id: "Roboto",
      name: `Roboto`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Roboto").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Roboto" }),
    },
    {
      id: "OpenSans",
      name: `Open Sans`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Open Sans").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Open Sans" }),
    },
    {
      id: "Lato",
      name: `Lato`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Lato").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Lato" }),
    },
    {
      id: "Montserrat",
      name: `Montserrat`,
      onClick: () => editor?.chain().focus("all").setFontFamily("Montserrat").run(),
      active: editor?.isActive("textStyle", { fontFamily: "Montserrat" }),
    },
  ];

  const textAlignOptions = [
    {
      id: "TextLeft",
      icon: <AlignLeft className="h-5 w-5" />,
      name: "left",
      onClick: () => editor?.chain().focus().setTextAlign("left").run(),
      active: editor?.isActive({ textAlign: "left" }),
    },
    {
      id: "TextCenter",
      icon: <AlignCenter className="h-5 w-5" />,
      name: "center",
      onClick: () => editor?.chain().focus().setTextAlign("center").run(),
      active: editor?.isActive({ textAlign: "center" }),
    },
    {
      id: "TextRight",
      icon: <AlignRight className="h-5 w-5" />,
      name: "right",
      onClick: () => editor?.chain().focus().setTextAlign("right").run(),
      active: editor?.isActive({ textAlign: "right" }),
    },
    {
      id: "TextJustify",
      icon: <AlignJustify className="h-3.5 w-3.5 mx-0.5 mr-1" />,
      name: "justify",
      onClick: () => editor?.chain().focus().setTextAlign("justify").run(),
      active: editor?.isActive({ textAlign: "justify" }),
    },
  ];

  const formattingOptions = [
    {
      id: "TextBold",
      icon: <Bold01 className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().toggleBold().run(),
      active: editor?.isActive("bold"),
    },
    {
      id: "TextItalic",
      icon: <Italic01 className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().toggleItalic().run(),
      active: editor?.isActive("italic"),
    },
    {
      id: "TextUnderline",
      icon: <Underline01 className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().toggleUnderline().run(),
      active: editor?.isActive("underline"),
    },
    {
      id: "TextStrike",
      icon: <Strikethrough01 className="w-5 h-5" />,
      onClick: () => editor?.chain().focus().toggleStrike().run(),
      active: editor?.isActive("strike"),
    },
  ];

  const onChangeColorEditor = (e: any) => editor?.chain().focus().setColor(e?.target.value).run();
  const colorEditor = editor?.getAttributes("textStyle").color;

  return (
    <div className={styles().base()}>
      {!isDownload && (
        <CertificateEditorHeader
          otherOptions={otherOptions}
          formattingOptions={formattingOptions}
          headingsOptions={headingsOptions}
          textAlignOptions={textAlignOptions}
          fontFamilyOptions={fontFamilyOptions}
          colorEditor={colorEditor}
          onChangeColorEditor={onChangeColorEditor}
          handleChangeOrientation={handleChangeOrientation}
        />
      )}
      <CertificateEditorContent editor={editor} fontFamilyOptions={fontFamilyOptions} />
    </div>
  );
};
